import React, { useState } from "react";
import { Phone, Mail, Check, ExternalLink, Bot } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "../components/motion";
import { PageHero, CtaBand, SocialRow } from "../components/shared";
import { openAiChat } from "../aichat";
import { QuoteForm } from "../components/QuotePanel";
import { CONTACT, img } from "../data/site";

const API_SEGN = `${process.env.REACT_APP_BACKEND_URL}/api/segnalazioni`;
const SEND_FAIL_MSG = "Non siamo riusciti a inviare il reclamo. Chiamaci al +39 06 232514 o scrivici in chat all'assistente AI";

function SegnalazioneForm() {
  const [s, setS] = useState({ messaggio: "", nome: "", email: "", sito: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!s.messaggio.trim()) return setErr("Scrivi il reclamo prima di inviare.");
    setErr("");
    setSending(true);
    try {
      const res = await fetch(API_SEGN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...s, provenance: "/contatti" }),
      });
      if (!res.ok) throw new Error("server");
      setDone(true);
    } catch {
      setErr(SEND_FAIL_MSG);
    } finally {
      setSending(false);
    }
  };
  return (
    <form onSubmit={submit} noValidate data-testid="seg-form">
      {/* Honeypot antispam: campo invisibile per gli umani, i bot lo compilano */}
      <input
        type="text"
        name="sito"
        value={s.sito}
        onChange={(e) => setS({ ...s, sito: e.target.value })}
        className="hp-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        data-testid="hp-field"
      />
      <div className="field" style={{ marginBottom: 12 }}>
        <label htmlFor="seg-messaggio">Il tuo reclamo *</label>
        <textarea id="seg-messaggio" value={s.messaggio} onChange={set("messaggio")} data-testid="seg-messaggio" />
      </div>
      <div className="frow" style={{ marginBottom: 4 }}>
        <div className="field">
          <label htmlFor="seg-nome">Nome (facoltativo)</label>
          <input id="seg-nome" value={s.nome} onChange={set("nome")} data-testid="seg-nome" autoComplete="name" />
        </div>
        <div className="field">
          <label htmlFor="seg-email">Email (facoltativa, per risponderti)</label>
          <input id="seg-email" type="email" value={s.email} onChange={set("email")} data-testid="seg-email" autoComplete="email" />
        </div>
      </div>
      {done ? (
        <p style={{ color: "#0c7a3e", fontWeight: 600 }} data-testid="seg-ok">
          Grazie, abbiamo ricevuto il tuo reclamo. Verrai ricontattato al più presto.
        </p>
      ) : (
        <>
          {err && <p className="ferr" data-testid="seg-fail">{err}</p>}
          <div className="fnav" style={{ marginTop: 16 }}>
            <button type="submit" className="btn dark sm" disabled={sending} data-testid="seg-invia" style={{ opacity: sending ? 0.6 : 1 }}>
              {sending ? "Invio…" : "Invia il reclamo"}
            </button>
            <a className="btn white sm" style={{ boxShadow: "inset 0 0 0 2px var(--line)" }} href="#assistente-ai" onClick={(e) => { e.preventDefault(); openAiChat(); }} data-testid="seg-chat">
              Chat con l'assistente AI
            </a>
          </div>
        </>
      )}
    </form>
  );
}

export default function Contatti() {
  return (
    <>
      <PageHero
        kick="Contatti"
        lines={["Parliamo della", "tua sicurezza."]}
        sub="Rispondiamo a qualsiasi ora: al telefono e in chat con l'assistente Keplero, che raccoglie la tua richiesta e poi un nostro ufficio ti richiama, sia che sei un cliente sia che sei un candidato in cerca di lavoro."
        ph={img("sede-san-cesareo-drone-1.jpg")}
      />

      <section className="sec" data-testid="contatti-cards">
        <div className="wrap">
          <div className="contact-cards">
            {[
              { ic: <Phone size={20} />, href: CONTACT.phoneHref, t: CONTACT.phone, s: "Telefono, assistente virtuale 24/7", tid: "contact-phone", ext: false },
              { ic: <Bot size={22} />, t: "Chat con l'assistente AI", s: "Cliente o candidato? Scrivi subito in chat", tid: "contact-ai-chat", chat: true },
              { ic: <Mail size={20} />, href: `mailto:${CONTACT.email}`, t: CONTACT.email, s: "Ufficio commerciale", tid: "contact-email", ext: false },
            ].map((c) => (
              <Reveal key={c.tid}>
                <a
                  className="cc"
                  href={c.chat ? "#assistente-ai" : c.href}
                  target={c.ext ? "_blank" : undefined}
                  rel={c.ext ? "noreferrer" : undefined}
                  onClick={c.chat ? (e) => { e.preventDefault(); openAiChat(); } : undefined}
                  data-testid={c.tid}
                >
                  <span className="ic">{c.ic}</span>
                  <strong>{c.t}</strong>
                  <span>{c.s}</span>
                </a>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.12}>
            <div style={{ marginTop: 26, display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }} data-testid="contatti-social">
              <p className="kick" style={{ margin: 0 }}>Seguici</p>
              <SocialRow testid="social-contatti" onLight />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="sec" style={{ paddingTop: 0 }} data-testid="contatti-form">
        <div className="wrap" style={{ display: "flex", flexWrap: "wrap", gap: "48px 80px" }}>
          <Reveal style={{ flex: "1 1 380px", minWidth: 0 }}>
            <p className="kick">Preventivo gratuito</p>
            <h2 className="h2">Due passaggi, un minuto.</h2>
            <p className="lead" style={{ margin: "26px 0 0" }}>
              Dicci per chi è il servizio e di cosa hai bisogno: un nostro responsabile ti ricontatta per il sopralluogo gratuito.
            </p>
            <p style={{ margin: "22px 0 0", padding: "18px 20px", background: "var(--mist)", borderRadius: 16, fontSize: 15.5, color: "var(--muted)" }} data-testid="ai-note">
              Quando chiami o scrivi in chat all'assistente AI, ti fa qualche domanda, come in una normale conversazione, per capire di cosa hai bisogno e raccoglie la tua richiesta: vale per i clienti e anche per i candidati che si vogliono candidare per un lavoro. Poi un nostro ufficio ti richiama per seguirti di persona.
            </p>
          </Reveal>

          <Reveal delay={0.08} style={{ flex: "1 1 560px", minWidth: 0 }}>
            <QuoteForm provenance="/contatti" />
          </Reveal>
        </div>
      </section>

      <section className="sec-mist sec" data-testid="contatti-sede">
        <div className="wrap" style={{ display: "flex", flexWrap: "wrap", gap: "48px 80px" }}>
          <Reveal style={{ flex: "1 1 420px", minWidth: 0 }}>
            <p className="kick">Sede generale</p>
            <h2 className="h2">San Cesareo, Roma</h2>
            <p style={{ margin: "24px 0 26px", color: "var(--muted)", lineHeight: 1.9 }} data-testid="sede-dettagli">
              Orion Security Srl
              <br />
              {CONTACT.address}
              <br />
              Tel. {CONTACT.phone} · Fax {CONTACT.fax}
              <br />
              {CONTACT.email} · {CONTACT.altEmail}
            </p>
            <a
              className="btn dark sm"
              href="https://www.google.com/maps/search/?api=1&query=Via+Prusst+San+Cesareo+RM"
              target="_blank"
              rel="noreferrer"
              data-testid="sede-maps"
            >
              Apri in Google Maps <ExternalLink size={16} />
            </a>
          </Reveal>
          <Reveal delay={0.08} style={{ flex: "1 1 420px", minWidth: 0, display: "grid", gap: 16 }}>
            <div className="mini" data-testid="lavora-card">
              <div className="mic">
                <Check size={20} />
              </div>
              <h3>Lavora con noi</h3>
              <p style={{ marginBottom: 16 }}>Guardie giurate, addetti aeroportuali, operatori fiduciari e di centrale. Consulta le posizioni aperte.</p>
              <a href={CONTACT.careerUrl} className="btn dark sm" target="_blank" rel="noreferrer" data-testid="lavora-cta">
                Posizioni aperte
              </a>
            </div>
            <div className="mini" data-testid="segnalazioni-card">
              <div className="mic">
                <Mail size={20} />
              </div>
              <h3>Reclami sul servizio</h3>
              <p style={{ marginBottom: 16 }}>
                Sei un cliente e hai riscontrato un problema sul servizio? Descrivici il problema: il reclamo arriva via email all'ufficio commerciale e verrai ricontattato al più presto.
              </p>
              <SegnalazioneForm />
              <p style={{ marginTop: 18, marginBottom: 0, color: "var(--muted)", fontSize: 14 }} data-testid="whistleblowing-note">
                Per segnalare illeciti o violazioni (whistleblowing) è disponibile un canale riservato dedicato: <Link to="/whistleblowing" data-testid="whistleblowing-link">vai alla pagina Whistleblowing</Link>.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand testid="contatti-cta" />
    </>
  );
}
